import { Check, Close, Delete, ExpandMore, Pause, PlayArrow, Stop } from '@mui/icons-material';
import { Collapse, IconButton, Tooltip, Typography } from '@mui/material';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import { DateTime } from 'luxon';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useState } from 'react';

import { Input } from '@/components/Input';
import { useStopwatchStore } from '@/configs/store';
import { TaskQueueSelector } from '@/features/draftTimelogs/ui/TaskQueueSelector';
import { Stopwatch } from '@/types';
import { formatDurationRussian } from '@/utils/formatDurationRussian';

import { useDebouncedComment, useElapsedSeconds, useLogAtEditor, useTimeEditor } from './hooks';
import { calcElapsedSeconds, formatDigitalTime } from './utils';

import s from './StopwatchCard.module.scss';

type StopwatchCardProps = {
  stopwatch: Stopwatch;
  onFinish: (stopwatch: Stopwatch, wasRunning: boolean) => void;
};

const CIRCLE_SIZE = 140;
const CENTER = CIRCLE_SIZE / 2;
const RADIUS = 58;
const STROKE_WIDTH = 5;

const NUM_SEGMENTS = 12;
const CYCLE_DURATION = 7;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
const SEGMENT_ARC = CIRCUMFERENCE / NUM_SEGMENTS;
const DASH_LENGTH = SEGMENT_ARC * 0.78;
const DASH_REST = CIRCUMFERENCE - DASH_LENGTH;

const STAGGER = CYCLE_DURATION / NUM_SEGMENTS;

const SPINNER_SEGMENTS = Array.from({ length: NUM_SEGMENTS }, (_, i) => ({
  dashOffset: -i * SEGMENT_ARC,
  delay: -(NUM_SEGMENTS - 1 - i) * STAGGER,
}));

export const StopwatchCard = observer(({ stopwatch, onFinish }: StopwatchCardProps) => {
  const store = useStopwatchStore();
  const displaySeconds = useElapsedSeconds(stopwatch, store.clockOffset);
  const timeEditor = useTimeEditor(stopwatch, store);
  const logAtEditor = useLogAtEditor(stopwatch, store);
  const commentState = useDebouncedComment(stopwatch, store);

  const [isActing, setIsActing] = useState(false);
  const [titleExpanded, setTitleExpanded] = useState(false);

  useEffect(() => {
    setTitleExpanded(false);
  }, [stopwatch.taskKey, stopwatch.taskTitle]);

  const isRunning = stopwatch.status === 'running';

  const handleToggle = useCallback(async () => {
    setIsActing(true);

    if (isRunning) {
      const currentSeconds = calcElapsedSeconds(stopwatch, store.clockOffset);
      await store.pauseStopwatch(stopwatch.id, currentSeconds, stopwatch.startedAt!);
    } else {
      await store.startStopwatch(stopwatch.id);
    }

    setIsActing(false);
  }, [isRunning, stopwatch, store]);

  const handleDelete = useCallback(async () => {
    setIsActing(true);
    await store.deleteStopwatch(stopwatch.id);
    setIsActing(false);
  }, [stopwatch.id, store]);

  const handleFinish = useCallback(async () => {
    setIsActing(true);

    if (isRunning) {
      const currentSeconds = calcElapsedSeconds(stopwatch, store.clockOffset);
      await store.pauseStopwatch(stopwatch.id, currentSeconds, stopwatch.startedAt!);
    }

    const fresh = store.stopwatches.find((sw) => sw.id === stopwatch.id);
    onFinish(fresh ?? stopwatch, isRunning);
    setIsActing(false);
  }, [isRunning, stopwatch, store, onFinish]);

  const handleTaskChange = useCallback(
    async (next: {
      taskKey?: string | null;
      taskTitle?: string | null;
      queueKey?: string | null;
      queueName?: string | null;
    }) => {
      await store.updateStopwatch(stopwatch.id, {
        taskKey: next.taskKey ?? null,
        taskTitle: next.taskTitle ?? null,
        queueKey: next.queueKey ?? null,
        queueName: next.queueName ?? null,
      });
    },
    [stopwatch.id, store],
  );

  const handleTaskClear = useCallback(async () => {
    await store.updateStopwatch(stopwatch.id, {
      taskKey: null,
      taskTitle: null,
      queueKey: null,
      queueName: null,
    });
  }, [stopwatch.id, store]);

  return (
    <div className={s.card}>
      <div className={s.card__timerBlock}>
        {/* Circle Timer */}
        <div className={s.card__circle}>
          <svg
            width={CIRCLE_SIZE}
            height={CIRCLE_SIZE}
            viewBox={`0 0 ${CIRCLE_SIZE} ${CIRCLE_SIZE}`}
            className={s.card__svg}
          >
            <circle
              cx={CENTER}
              cy={CENTER}
              r={RADIUS}
              fill="none"
              stroke="#e8e8e8"
              strokeWidth={STROKE_WIDTH}
            />
            {isRunning && (
              <g transform={`rotate(-90 ${CENTER} ${CENTER})`}>
                {SPINNER_SEGMENTS.map(({ dashOffset, delay }, i) => (
                  <circle
                    key={i}
                    cx={CENTER}
                    cy={CENTER}
                    r={RADIUS}
                    fill="none"
                    stroke="var(--accent-color)"
                    strokeWidth={STROKE_WIDTH}
                    strokeDasharray={`${DASH_LENGTH} ${DASH_REST}`}
                    strokeDashoffset={dashOffset}
                    strokeLinecap="round"
                    className={s.card__segment}
                    style={{ animationDelay: `${delay}s` }}
                  />
                ))}
              </g>
            )}
          </svg>

          <div className={s.card__center}>
            <span
              className={clsx(
                s.card__time,
                !isRunning && !timeEditor.editing && s.card__time_editable,
              )}
              onClick={timeEditor.handleStartEdit}
              title={!isRunning && !timeEditor.editing ? 'Нажмите для редактирования' : undefined}
            >
              {formatDigitalTime(displaySeconds)}
            </span>
          </div>
        </div>

        {stopwatch.taskKey && (
          <div className={s.card__taskMeta}>
            <div className={s.card__taskKeyRow} onClick={() => setTitleExpanded((open) => !open)}>
              <span className={s.card__taskKey}>{stopwatch.taskKey}</span>
              {stopwatch.taskTitle ? (
                <IconButton
                  size="small"
                  aria-expanded={titleExpanded}
                  className={s.card__expandBtn}
                >
                  <ExpandMore
                    fontSize="small"
                    className={clsx(s.card__expandIcon, titleExpanded && s.card__expandIcon_open)}
                  />
                </IconButton>
              ) : null}
            </div>
            <Collapse
              in={titleExpanded && Boolean(stopwatch.taskTitle)}
              timeout="auto"
              unmountOnExit
            >
              <Typography variant="body2" color="text.secondary" className={s.card__taskTitle}>
                {stopwatch.taskTitle}
              </Typography>
            </Collapse>
          </div>
        )}
      </div>

      {/* Time Editor — below circle */}
      <AnimatePresence>
        {timeEditor.editing && (
          <motion.div
            className={s.card__editor}
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.15 }}
          >
            <div className={s['card__editor-row']}>
              <Input
                autoFocus
                value={timeEditor.timeInput}
                onChange={(e) => timeEditor.setTimeInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && timeEditor.isTimeValid) timeEditor.handleSave();

                  if (e.key === 'Escape') timeEditor.handleCancel();
                }}
                placeholder="1ч 30м"
                sx={{ flex: 1, '& input': { textAlign: 'center', fontSize: 14, py: 0.75 } }}
              />
              <IconButton
                size="small"
                disabled={!timeEditor.isTimeValid}
                onClick={timeEditor.handleSave}
                color="primary"
              >
                <Check fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={timeEditor.handleCancel}>
                <Close fontSize="small" color="error" />
              </IconButton>
            </div>
            {timeEditor.timeInput.trim() &&
              timeEditor.parsedTimeMinutes !== null &&
              timeEditor.parsedTimeMinutes > 0 && (
                <Typography variant="caption" color="text.secondary">
                  = {formatDurationRussian(timeEditor.parsedTimeMinutes)}
                </Typography>
              )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Log-at */}
      <AnimatePresence mode="wait">
        {logAtEditor.editing ? (
          <motion.div
            key="log-at-editor"
            className={s['card__log-at-editor']}
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.15 }}
          >
            <div className={s['card__log-at-editor-row']}>
              <Input
                value={logAtEditor.dateValue}
                onChange={(e) => logAtEditor.setDateValue(e.target.value)}
                type="date"
                sx={{ flex: 1, '& input': { fontSize: 13, py: 0.75 } }}
              />
              <Input
                value={logAtEditor.timeValue}
                onChange={(e) => logAtEditor.setTimeValue(e.target.value)}
                type="time"
                sx={{ width: 100, '& input': { fontSize: 13, py: 0.75 } }}
              />
              <IconButton
                size="small"
                disabled={!logAtEditor.isValid}
                onClick={logAtEditor.handleSave}
                color="primary"
              >
                <Check fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={logAtEditor.handleCancel}>
                <Close fontSize="small" color="error" />
              </IconButton>
            </div>
          </motion.div>
        ) : (
          <motion.span
            key="log-at-display"
            className={s['card__log-at']}
            onClick={logAtEditor.handleStartEdit}
            title="Нажмите для редактирования"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
          >
            {DateTime.fromISO(stopwatch.logAt).toFormat('dd.MM, HH:mm')}
          </motion.span>
        )}
      </AnimatePresence>

      {/* Action Buttons */}
      <div className={s.card__actions}>
        <Tooltip title="Удалить">
          <span>
            <IconButton onClick={handleDelete} disabled={isActing} size="small">
              <Delete fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title={isRunning ? 'Пауза' : 'Старт'}>
          <span>
            <IconButton
              onClick={handleToggle}
              disabled={isActing}
              sx={{
                width: 48,
                height: 48,
                bgcolor: isRunning ? '#ef5350' : 'var(--accent-color)',
                color: 'white',
                '&:hover': {
                  bgcolor: isRunning ? '#d32f2f' : 'var(--accent-hover-color)',
                },
              }}
            >
              {isRunning ? <Pause /> : <PlayArrow />}
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Завершить">
          <span>
            <IconButton onClick={handleFinish} disabled={isActing} size="small">
              <Stop fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </div>

      {/* Task & Comment */}
      <div className={s.card__details}>
        <TaskQueueSelector
          value={{
            taskKey: stopwatch.taskKey,
            taskTitle: stopwatch.taskTitle,
            queueKey: stopwatch.queueKey,
            queueName: stopwatch.queueName,
          }}
          onChange={handleTaskChange}
          onClear={handleTaskClear}
        />
        <Input
          value={commentState.comment}
          onChange={(e) => commentState.setComment(e.target.value)}
          onBlur={commentState.handleBlur}
          placeholder="Комментарий..."
        />
      </div>
    </div>
  );
});
